import { useState } from "react";
import { Link } from "wouter";
import { CarLogo } from "../components/common/CarLogo.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { Paginacao } from "../components/common/Paginacao.js";
import {
  CHIP_OFF,
  CHIP_ON,
  FILTER_INPUT,
  FilterField,
  FilterPanel,
} from "../components/common/FilterBar.js";
import { Search } from "lucide-react";

/**
 * F-devis-list (CEO 2026-09-20) — "Liste des devis", como no eDSP.
 *
 * Um DEVIS é só um orçamento: nem todo devis vira reparo. Esta tela é o funil
 * — o que está orçado, o que entrou em reparação (com o nº da ordem) e o que
 * já foi faturado.
 */

const STAGES = ["all", "devis", "repairing", "invoiced"] as const;
type Stage = (typeof STAGES)[number];

export default function Devis() {
  const t = useT();
  const { user } = useAuth();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId] = useSelectedCompany(accessibleCompanies);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState<Stage>("all");
  const [page, setPage] = useState(0);
  const PAGE = 50;

  const listQuery = trpc.dossiers.devisList.useQuery(
    {
      companyId: companyId ?? 0,
      q: q.trim() || undefined,
      stage,
      limit: PAGE,
      offset: page * PAGE,
    },
    { enabled: companyId !== null },
  );
  const rows = listQuery.data?.rows ?? [];
  const total = listQuery.data?.total ?? 0;

  const stageLabel: Record<string, string> = {
    devis: t("devisPage.stageDevis"),
    repairing: t("devisPage.stageRepairing"),
    invoiced: t("devisPage.stageInvoiced"),
  };
  const stageCls: Record<string, string> = {
    devis: "bg-lime/15 text-lime",
    repairing: "bg-amber-500/15 text-amber-300",
    invoiced: "bg-neutral-700 text-neutral-300",
  };

  return (
    <ErpShell>
      <div className="mb-3">
        <FilterPanel testId="devis-filter-panel">
          <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
            <FilterField label={t("devisPage.filterSearch")} className="grow sm:max-w-sm">
              <div className="relative">
                <Search
                  size={16}
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--text-muted)" }}
                />
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(0);
                  }}
                  placeholder={t("devisPage.searchPlaceholder")}
                  className={FILTER_INPUT + " w-full pl-9"}
                />
              </div>
            </FilterField>
            <FilterField label={t("devisPage.filterStage")}>
              <div className="flex flex-wrap gap-1.5">
                {STAGES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setStage(s);
                      setPage(0);
                    }}
                    aria-pressed={stage === s}
                    className={stage === s ? CHIP_ON : CHIP_OFF}
                  >
                    {s === "all" ? t("devisPage.stageAll") : stageLabel[s]}
                  </button>
                ))}
              </div>
            </FilterField>
            <span className="ml-auto pb-2 text-sm" style={{ color: "var(--text-muted)" }}>
              <b style={{ color: "var(--text-strong)" }}>{total}</b> {t("devisPage.countSuffix")}
            </span>
          </div>
        </FilterPanel>
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-800">
        <table className="w-full text-sm">
          <thead className="bg-neutral-950/50 text-[11px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-3 py-2 text-left">{t("devisPage.colVehicle")}</th>
              <th className="px-3 py-2 text-left">{t("devisPage.colCustomer")}</th>
              <th className="px-3 py-2 text-left">{t("devisPage.colDevis")}</th>
              <th className="px-3 py-2 text-right">{t("devisPage.colTotal")}</th>
              <th className="px-3 py-2 text-right">{t("devisPage.colImpacts")}</th>
              <th className="px-3 py-2 text-left">{t("devisPage.colStage")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-neutral-900/60">
                <td className="px-3 py-2">
                  <Link href={`/dossiers/${r.id}`} className="flex items-center gap-2">
                    <CarLogo make={r.make ?? ""} size={22} />
                    <span>
                      <span className="block font-mono text-neutral-200">{r.plate ?? "—"}</span>
                      <span className="block text-xs text-neutral-500">
                        {[r.make, r.model].filter(Boolean).join(" ") || "—"}
                      </span>
                    </span>
                  </Link>
                </td>
                <td className="px-3 py-2 text-neutral-300">{r.customerName}</td>
                <td className="px-3 py-2">
                  <span className="block font-mono text-xs text-neutral-300">
                    {r.devisRef ?? r.dossierCode ?? "—"}
                  </span>
                  <span className="block text-xs text-neutral-500">{r.devisDate ?? ""}</span>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {formatMoney(r.totalHt, { decimals: 2 })}
                  {r.degarnissage > 0 && (
                    <span className="block text-[10px] text-neutral-500">
                      {t("devisPage.withDegarnissage")}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-400">
                  {r.impacts || "—"}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded px-2 py-0.5 text-[10px] font-semibold ${stageCls[r.stage]}`}
                  >
                    {stageLabel[r.stage]}
                  </span>
                  {r.orderNumber && (
                    <span className="ml-2 font-mono text-[10px] text-neutral-500">
                      {r.orderNumber}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !listQuery.isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-neutral-500">
                  {t("devisPage.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE && (
        <Paginacao
          total={total}
          pageSize={PAGE}
          offset={page * PAGE}
          onOffset={(o) => setPage(Math.floor(o / PAGE))}
          testId="devis-pagination"
        />
      )}
    </ErpShell>
  );
}
